import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

const STEPS = [
  { id: 'generator',     label: 'Generator Info' },
  { id: 'waste',         label: 'Waste Characterization' },
  { id: 'regulatory',    label: 'Regulatory' },
  { id: 'handling',      label: 'Handling & Safety' },
  { id: 'certification', label: 'Certification' },
];

const WASTE_SOURCES = [
  { label: 'Diesel Tanks',          starred: false },
  { label: 'Gasoline Tanks',        starred: false },
  { label: 'Fuel Oil Tanks',        starred: false },
  { label: 'Jet A Tanks',           starred: false },
  { label: 'Kerosene Tanks',        starred: false },
  { label: 'Sumps/Containments',    starred: false },
  { label: 'Storm Drains',          starred: true },
  { label: 'Transformers',          starred: true },
  { label: 'Electric Vaults',       starred: true },
  { label: 'Dielectric Fluid',      starred: true },
  { label: 'Monitoring Wells',      starred: true },
  { label: 'Oil Water Separators',  starred: true },
];

const EMPTY = {
  transporter_customer: '',
  company_name: '', contact_name: '', contact_title: '', contact_email: '',
  contact_phone: '', generator_address: '', generator_city: '', generator_state: '',
  generator_zip: '', epa_id: '',
  waste_name: '', waste_description: '', physical_state: '', color: '', odor: '',
  ph_min: '', ph_max: '', specific_gravity: '', flash_point_f: '', boiling_point_f: '',
  viscosity: '', constituents: [], waste_sources: [],
  is_hazardous: false, rcra_codes: '', dot_shipping_name: '', dot_un_number: '',
  dot_hazard_class: '', dot_packing_group: '',
  special_handling: '', incompatible_materials: '', ppe_required: '',
  emergency_contact: '', emergency_phone: '',
  certifier_name: '', certifier_title: '', certified: false,
};

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Copy all reusable fields from a profile — skip certification fields
function extractPrefillFields(p) {
  return {
    transporter_customer: p.transporter_customer || '',
    company_name:         p.company_name         || '',
    contact_name:         p.contact_name         || '',
    contact_title:        p.contact_title        || '',
    contact_email:        p.contact_email        || '',
    contact_phone:        p.contact_phone        || '',
    generator_address:    p.generator_address    || '',
    generator_city:       p.generator_city       || '',
    generator_state:      p.generator_state      || '',
    generator_zip:        p.generator_zip        || '',
    epa_id:               p.epa_id               || '',
    waste_name:           p.waste_name           || '',
    waste_description:    p.waste_description    || '',
    physical_state:       p.physical_state       || '',
    color:                p.color                || '',
    odor:                 p.odor                 || '',
    ph_min:               p.ph_min               || '',
    ph_max:               p.ph_max               || '',
    specific_gravity:     p.specific_gravity     || '',
    flash_point_f:        p.flash_point_f        || '',
    boiling_point_f:      p.boiling_point_f      || '',
    viscosity:            p.viscosity            || '',
    constituents:         p.constituents         || [],
    waste_sources:        p.waste_sources        || [],
    is_hazardous:         p.is_hazardous         || false,
    rcra_codes:           Array.isArray(p.rcra_codes) ? p.rcra_codes.join(', ') : (p.rcra_codes || ''),
    dot_shipping_name:    p.dot_shipping_name    || '',
    dot_un_number:        p.dot_un_number        || '',
    dot_hazard_class:     p.dot_hazard_class     || '',
    dot_packing_group:    p.dot_packing_group    || '',
    special_handling:     p.special_handling     || '',
    incompatible_materials: p.incompatible_materials || '',
    ppe_required:         Array.isArray(p.ppe_required) ? p.ppe_required.join(', ') : (p.ppe_required || ''),
    emergency_contact:    p.emergency_contact    || '',
    emergency_phone:      p.emergency_phone      || '',
    // certifier_name, certifier_title, certified intentionally omitted
  };
}

export default function ProfileForm() {
  const { id }     = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const isEdit     = Boolean(id);

  const [step, setStep]     = useState(0);
  const [form, setForm]     = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [profileId, setProfileId] = useState(id || null);

  // Multi-file lab attachments
  const [labFiles, setLabFiles]             = useState([]);
  const [labAttachments, setLabAttachments] = useState([]);
  const [fileUploadError, setFileUploadError] = useState('');

  const isAdmin = user && ['admin', 'superadmin'].includes(user.role);
  const [vendors, setVendors]               = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [vendorSearch, setVendorSearch]     = useState('');

  // ── Pre-fill / template state ──────────────────────────────────────────
  const [pastProfiles, setPastProfiles]         = useState([]);   // all vendor's previous profiles
  const [prefillProfile, setPrefillProfile]     = useState(null); // most recent, for banner
  const [showPrefillBanner, setShowPrefillBanner] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [prefillApplied, setPrefillApplied]     = useState(false);

  // ── Load data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) {
      apiGet('/users?role=vendor').then(list => setVendors(list || [])).catch(console.error);
    }

    if (isEdit) {
      // Edit mode: load existing profile
      apiGet(`/profiles/${id}`).then(p => {
        setForm({
          ...EMPTY, ...p,
          rcra_codes:   (p.rcra_codes   || []).join(', '),
          ppe_required: (p.ppe_required || []).join(', '),
          constituents: p.constituents  || [],
          waste_sources: p.waste_sources || [],
          certified: false,
        });
      }).catch(console.error);
      apiGet(`/attachments/profile/${id}`).then(atts => {
        if (atts && atts.length > 0) {
          setLabAttachments(atts.map(a => ({ id: a.id, filename: a.original_name, mimeType: a.mime_type })));
        }
      }).catch(console.error);

    } else if (user && !isAdmin) {
      // New profile for a vendor
      const fromId = new URLSearchParams(location.search).get('from');

      // Start with user defaults
      const userDefaults = {
        transporter_customer: user.companyName || '',
        company_name:   user.companyName || '',
        contact_name:   `${user.firstName} ${user.lastName}`,
        contact_email:  user.email || '',
        certifier_name: `${user.firstName} ${user.lastName}`,
      };
      setForm(f => ({ ...f, ...userDefaults }));

      // Load all past profiles for template picker + pre-fill banner
      apiGet('/profiles/my').then(profiles => {
        if (!profiles || profiles.length === 0) return;
        setPastProfiles(profiles);

        if (fromId) {
          // ?from=<id>: pre-fill directly from that specific profile
          const source = profiles.find(p => p.id === fromId);
          if (source) {
            setForm({ ...EMPTY, ...userDefaults, ...extractPrefillFields(source) });
            setPrefillApplied(true);
          } else {
            // profile not in list, fetch it directly
            apiGet(`/profiles/${fromId}`).then(p => {
              setForm({ ...EMPTY, ...userDefaults, ...extractPrefillFields(p) });
              setPrefillApplied(true);
            }).catch(console.error);
          }
        } else {
          // Show pre-fill banner with most recent submission
          const lastSubmitted = profiles.find(p => p.status !== 'draft') || profiles[0];
          if (lastSubmitted) {
            setPrefillProfile(lastSubmitted);
            setShowPrefillBanner(true);
          }
        }
      }).catch(console.error);
    }
  }, [id, isEdit, user, location.search]);

  function applyPrefill(sourceProfile) {
    setForm(f => ({ ...f, ...extractPrefillFields(sourceProfile) }));
    setShowPrefillBanner(false);
    setShowTemplatePicker(false);
    setPrefillApplied(true);
  }

  function dismissPrefill() {
    setShowPrefillBanner(false);
  }

  const set = k => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
    setErrors(er => ({ ...er, [k]: '' }));
  };

  function toggleSource(label) {
    setForm(f => {
      const exists = f.waste_sources.includes(label);
      return {
        ...f,
        waste_sources: exists ? f.waste_sources.filter(s => s !== label) : [...f.waste_sources, label]
      };
    });
    setErrors(er => ({ ...er, lab_file: '' }));
  }

  const needsLabUpload = form.waste_sources.some(
    s => WASTE_SOURCES.find(w => w.label === s && w.starred)
  );

  function handleLabFilesChange(e) {
    const picked = Array.from(e.target.files);
    e.target.value = '';
    setFileUploadError('');
    for (const file of picked) {
      if (file.size > 8 * 1024 * 1024) {
        setFileUploadError(`"${file.name}" exceeds the 8 MB limit and was skipped.`);
        continue;
      }
      if (labFiles.some(f => f.filename === file.name)) continue;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setLabFiles(prev => [...prev, { filename: file.name, mimeType: file.type, base64 }]);
      };
      reader.readAsDataURL(file);
    }
    setErrors(er => ({ ...er, lab_file: '' }));
  }

  function removePendingFile(filename) {
    setLabFiles(prev => prev.filter(f => f.filename !== filename));
  }

  async function removeUploadedAttachment(attId) {
    try {
      const token = localStorage.getItem('awwt_token');
      await fetch(`${process.env.REACT_APP_API_URL}/api/attachments/${attId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (err) {
      console.error('Failed to delete attachment', err);
    }
  }

  async function uploadPendingFiles(pid) {
    const uploaded = [];
    for (const lf of labFiles) {
      if (!lf.base64) continue;
      try {
        const att = await apiPost('/attachments', {
          profileId: pid,
          filename: lf.filename,
          mimeType: lf.mimeType,
          fileBase64: lf.base64,
        });
        uploaded.push({ id: att.id, filename: lf.filename, mimeType: lf.mimeType });
      } catch (err) {
        console.error('Failed to upload', lf.filename, err);
      }
    }
    if (uploaded.length > 0) {
      setLabAttachments(prev => [...prev, ...uploaded]);
      setLabFiles([]);
    }
  }

  function validate() {
    const e = {};
    if (step === 0) {
      if (!form.company_name)  e.company_name  = 'Required';
      if (!form.contact_name)  e.contact_name  = 'Required';
      if (!form.contact_email) e.contact_email = 'Required';
    }
    if (step === 1) {
      if (!form.waste_name)     e.waste_name     = 'Required';
      if (!form.physical_state) e.physical_state = 'Required';
      if (needsLabUpload && labFiles.length === 0 && labAttachments.length === 0) {
        e.lab_file = 'A lab analysis report must be attached for the selected source(s) before continuing';
      }
    }
    if (step === 2) {
      if (!form.dot_shipping_name) e.dot_shipping_name = 'Required';
    }
    if (step === 4) {
      if (!form.certifier_name)  e.certifier_name  = 'Required';
      if (!form.certifier_title) e.certifier_title = 'Required';
      if (!form.certified)       e.certified = 'You must certify accuracy of the information';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function buildPayload() {
    const payload = {
      ...form,
      waste_sources: form.waste_sources || [],
      rcra_codes:    form.rcra_codes    ? form.rcra_codes.split(',').map(s => s.trim()).filter(Boolean) : null,
      ppe_required:  form.ppe_required  ? form.ppe_required.split(',').map(s => s.trim()).filter(Boolean) : null,
      ph_min:           form.ph_min           || null,
      ph_max:           form.ph_max           || null,
      specific_gravity: form.specific_gravity || null,
      flash_point_f:    form.flash_point_f    || null,
      boiling_point_f:  form.boiling_point_f  || null,
    };
    if (isAdmin && selectedVendorId) payload.vendor_id = selectedVendorId;
    return payload;
  }

  async function saveDraft() {
    setSaving(true); setSaveMsg('');
    try {
      const payload = buildPayload();
      let pid = profileId;
      if (pid) {
        await apiPut(`/profiles/${pid}`, payload);
      } else {
        const saved = await apiPost('/profiles', payload);
        pid = saved.id;
        setProfileId(pid);
      }
      await uploadPendingFiles(pid);
      setSaveMsg('Draft saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleNext() {
    if (!validate()) return;
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    setErrors({});
    try {
      const payload = buildPayload();
      let pid = profileId;
      if (pid) {
        await apiPut(`/profiles/${pid}`, payload);
      } else {
        const saved = await apiPost('/profiles', payload);
        pid = saved.id;
        setProfileId(pid);
      }
      await uploadPendingFiles(pid);
      await apiPost(`/profiles/${pid}/submit`, {
        certifier_name:  form.certifier_name,
        certifier_title: form.certifier_title,
      });
      window.location.href = isAdmin ? '/admin/profiles' : '/vendor?submitted=true';
    } catch (err) {
      console.error('Submit error:', err);
      setErrors({ _submit: err.message || 'Submission failed. Please try again.' });
      setSubmitting(false);
    }
  }

  function addConstituent() {
    setForm(f => ({ ...f, constituents: [...f.constituents, { name: '', cas: '', percentage: '' }] }));
  }
  function updateConstituent(i, field, val) {
    setForm(f => {
      const c = [...f.constituents];
      c[i] = { ...c[i], [field]: val };
      return { ...f, constituents: c };
    });
  }
  function removeConstituent(i) {
    setForm(f => ({ ...f, constituents: f.constituents.filter((_, idx) => idx !== i) }));
  }

  const totalFiles = labAttachments.length + labFiles.length;

  // Helper: short label for a profile in the template picker
  function profileLabel(p) {
    const parts = [p.waste_name, p.company_name].filter(Boolean);
    return parts.join(' · ') || p.profile_number || 'Untitled';
  }

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Edit Waste Profile' : 'New Waste Profile'}</h1>
        <p>Complete all sections to submit for review.</p>
      </div>

      {isAdmin && !isEdit && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-section-title">Create on Behalf of Vendor</div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label>Search Vendor</label>
              <input
                placeholder="Type company name or email…"
                value={vendorSearch}
                onChange={e => setVendorSearch(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Select Vendor <span className="req">*</span></label>
              <select value={selectedVendorId} onChange={e => setSelectedVendorId(e.target.value)}>
                <option value="">— Select a vendor —</option>
                {vendors
                  .filter(v => {
                    if (!vendorSearch) return true;
                    const q = vendorSearch.toLowerCase();
                    return (v.company_name || '').toLowerCase().includes(q) ||
                           (v.email || '').toLowerCase().includes(q) ||
                           (`${v.first_name} ${v.last_name}`).toLowerCase().includes(q);
                  })
                  .map(v => (
                    <option key={v.id} value={v.id}>
                      {v.company_name || `${v.first_name} ${v.last_name}`} — {v.email}
                    </option>
                  ))
                }
              </select>
            </div>
          </div>
          {!selectedVendorId && (
            <p className="text-muted" style={{ marginTop: 4 }}>
              Select a vendor to assign this profile to before submitting.
            </p>
          )}
        </div>
      )}

      {/* ── Pre-fill banner (last submission) ──────────────────────── */}
      {showPrefillBanner && prefillProfile && !isEdit && !isAdmin && (
        <div style={{
          background: '#e8f5e9', border: '1px solid #a5d6a7',
          borderRadius: 'var(--radius)', padding: '14px 18px',
          marginBottom: 16, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
        }}>
          <div>
            <strong style={{ color: '#2e7d32' }}>Pre-fill from your last submission?</strong>
            <span style={{ fontSize: 13, color: '#388e3c', marginLeft: 8 }}>
              {profileLabel(prefillProfile)}
              {prefillProfile.profile_number ? ` (${prefillProfile.profile_number})` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm"
              style={{ background: '#2e7d32', color: '#fff', border: 'none' }}
              onClick={() => applyPrefill(prefillProfile)}
            >
              Yes, pre-fill
            </button>
            <button className="btn btn-secondary btn-sm" onClick={dismissPrefill}>
              Start fresh
            </button>
          </div>
        </div>
      )}

      {/* ── Template picker ─────────────────────────────────────────── */}
      {!isEdit && !isAdmin && pastProfiles.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {!showTemplatePicker ? (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowTemplatePicker(true)}
              style={{ fontSize: 12 }}
            >
              📋 Start from a saved profile / template
            </button>
          ) : (
            <div style={{
              background: 'var(--cream)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '14px 18px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>Choose a profile to use as a starting point</strong>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowTemplatePicker(false)}
                  style={{ fontSize: 11 }}
                >
                  ✕ Close
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                {pastProfiles.map(p => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--white)', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '8px 12px',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{p.waste_name || 'Untitled'}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-lt)' }}>
                        {p.company_name && <span>{p.company_name} · </span>}
                        {p.profile_number && <span>{p.profile_number} · </span>}
                        <span className={`badge badge-${p.status}`} style={{ fontSize: 10, padding: '1px 6px' }}>
                          {p.status}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 12, flexShrink: 0 }}
                      onClick={() => applyPrefill(p)}
                    >
                      Use this
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-muted" style={{ marginTop: 8, fontSize: 11 }}>
                Selecting a profile copies all fields except certification. You'll still need to certify and submit as a new profile.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Pre-fill applied confirmation ────────────────────────────── */}
      {prefillApplied && (
        <div style={{
          background: '#f3f8f3', border: '1px solid #c8dcc8',
          borderRadius: 'var(--radius)', padding: '8px 14px',
          marginBottom: 12, fontSize: 12, color: '#2d6a4f',
        }}>
          ✓ Fields pre-filled from a previous profile. Review and edit anything that has changed, then submit as a new profile.
        </div>
      )}

      {/* ── Step tabs ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 32, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {STEPS.map((s, i) => (
          <button key={s.id}
            onClick={() => i < step && setStep(i)}
            style={{
              flex: 1, padding: '12px 8px', border: 'none', cursor: i < step ? 'pointer' : 'default',
              background: i === step ? 'var(--forest)' : i < step ? 'var(--cream-dk)' : 'var(--white)',
              color: i === step ? 'var(--white)' : i < step ? 'var(--forest)' : 'var(--ink-lt)',
              fontFamily: 'inherit', fontSize: 12, fontWeight: i === step ? 600 : 400,
              borderRight: i < STEPS.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'all .15s',
            }}>
            <div style={{ fontSize: 10, opacity: .6, marginBottom: 2 }}>Step {i + 1}</div>
            {s.label}
          </button>
        ))}
      </div>

      <div className="card">
        {errors._submit && <div className="alert alert-error">{errors._submit}</div>}

        {/* ── STEP 0: Generator Info ──────────────────────────────────── */}
        {step === 0 && (
          <div>
            <div className="form-section-title">Transporter / Customer</div>
            <div className="form-grid" style={{ marginBottom: 16 }}>
              <Field label="Transporter / Customer Name">
                <input value={form.transporter_customer} onChange={set('transporter_customer')} readOnly style={{ background: 'var(--cream)', color: 'var(--ink-mid)' }} />
              </Field>
            </div>
            <div className="form-section-title">Generator / Site Information</div>
            <div className="form-grid form-grid-2">
              <Field label="Generator Name" req error={errors.company_name}>
                <input value={form.company_name} onChange={set('company_name')} />
              </Field>
              <Field label="EPA ID Number" error={errors.epa_id}>
                <input value={form.epa_id} onChange={set('epa_id')} placeholder="e.g. NYD000000000" />
              </Field>
            </div>
            <div style={{ height: 16 }} />
            <div className="form-section-title">Primary Contact</div>
            <div className="form-grid form-grid-2">
              <Field label="Contact Name" req error={errors.contact_name}>
                <input value={form.contact_name} onChange={set('contact_name')} />
              </Field>
              <Field label="Title / Position" error={errors.contact_title}>
                <input value={form.contact_title} onChange={set('contact_title')} />
              </Field>
              <Field label="Email Address" req error={errors.contact_email}>
                <input type="email" value={form.contact_email} onChange={set('contact_email')} />
              </Field>
              <Field label="Phone Number" error={errors.contact_phone}>
                <input type="tel" value={form.contact_phone} onChange={set('contact_phone')} />
              </Field>
            </div>
            <div style={{ height: 16 }} />
            <div className="form-section-title">Generator Site Address</div>
            <div className="form-grid">
              <Field label="Street Address">
                <input value={form.generator_address} onChange={set('generator_address')} />
              </Field>
            </div>
            <div className="form-grid form-grid-3" style={{ marginTop: 12 }}>
              <Field label="City">
                <input value={form.generator_city} onChange={set('generator_city')} />
              </Field>
              <Field label="State">
                <select value={form.generator_state} onChange={set('generator_state')}>
                  <option value="">— Select —</option>
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="ZIP Code">
                <input value={form.generator_zip} onChange={set('generator_zip')} />
              </Field>
            </div>
          </div>
        )}

        {/* ── STEP 1: Waste Characterization ─────────────────────────── */}
        {step === 1 && (
          <div>
            <div className="form-section-title">Waste Identification</div>
            <div className="form-grid form-grid-2">
              <Field label="Waste Name / Description" req error={errors.waste_name}>
                <input value={form.waste_name} onChange={set('waste_name')} placeholder="e.g. Spent Caustic Solution" />
              </Field>
              <Field label="Physical State" req error={errors.physical_state}>
                <select value={form.physical_state} onChange={set('physical_state')}>
                  <option value="">— Select —</option>
                  <option value="liquid">Liquid</option>
                  <option value="solid">Solid</option>
                  <option value="semi_solid">Semi-Solid / Sludge</option>
                </select>
              </Field>
            </div>

            <div style={{ height: 16 }} />
            <div className="form-section-title">Detailed Waste Description/Source</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px', marginBottom: 8 }}>
              {WASTE_SOURCES.map(src => (
                <label key={src.label} className="checkbox-group" style={{ fontSize: 13.5 }}>
                  <input
                    type="checkbox"
                    checked={form.waste_sources.includes(src.label)}
                    onChange={() => toggleSource(src.label)}
                  />
                  {src.label}
                  {src.starred && <span style={{ color: '#c62828', marginLeft: 2 }}>*</span>}
                </label>
              ))}
            </div>
            <p className="text-muted" style={{ marginBottom: 16 }}>* Requires mandatory lab analysis upload before you can continue.</p>

            {needsLabUpload && (
              <div className="alert alert-warn" style={{ marginBottom: 16 }}>
                <strong>Lab analysis required.</strong> One or more selected sources require lab analysis reports to be attached before you can proceed.

                {totalFiles > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {labAttachments.map(att => (
                      <div key={att.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#fff8e1', border: '1px solid #ffe082',
                        borderRadius: 6, padding: '6px 10px', fontSize: 12.5,
                      }}>
                        <span style={{ color: 'var(--forest)' }}>✓ {att.filename} <span style={{ color: 'var(--ink-lt)' }}>(uploaded)</span></span>
                        <button
                          type="button"
                          onClick={() => removeUploadedAttachment(att.id)}
                          style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                          title="Remove"
                        >✕</button>
                      </div>
                    ))}
                    {labFiles.map(lf => (
                      <div key={lf.filename} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#f1f8f4', border: '1px solid #c8dcc8',
                        borderRadius: 6, padding: '6px 10px', fontSize: 12.5,
                      }}>
                        <span style={{ color: 'var(--forest)' }}>📎 {lf.filename} <span style={{ color: 'var(--ink-lt)' }}>(will upload on save)</span></span>
                        <button
                          type="button"
                          onClick={() => removePendingFile(lf.filename)}
                          style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                          title="Remove"
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{
                    display: 'inline-block', padding: '7px 14px', fontSize: 13,
                    background: 'var(--forest)', color: '#fff', borderRadius: 6,
                    cursor: 'pointer', fontWeight: 600,
                  }}>
                    + Add Lab File{totalFiles > 0 ? 's' : ''}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      multiple
                      onChange={handleLabFilesChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {totalFiles > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--ink-mid)' }}>
                      {totalFiles} file{totalFiles !== 1 ? 's' : ''} attached
                    </span>
                  )}
                </div>

                {fileUploadError && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#c62828' }}>{fileUploadError}</div>
                )}
                {errors.lab_file && (
                  <div className="form-error" style={{ marginTop: 6 }}>{errors.lab_file}</div>
                )}
              </div>
            )}

            <div style={{ height: 16 }} />
            <div className="form-section-title">Physical Properties</div>
            <div className="form-grid form-grid-3">
              <Field label="Color">
                <input value={form.color} onChange={set('color')} placeholder="e.g. Clear, Dark brown" />
              </Field>
              <Field label="Odor">
                <input value={form.odor} onChange={set('odor')} placeholder="e.g. Mild, Pungent" />
              </Field>
              <Field label="Viscosity">
                <input value={form.viscosity} onChange={set('viscosity')} placeholder="e.g. Water-thin, Thick" />
              </Field>
              <Field label="pH (Min)">
                <input type="number" step="0.1" min="0" max="14" value={form.ph_min} onChange={set('ph_min')} />
              </Field>
              <Field label="pH (Max)">
                <input type="number" step="0.1" min="0" max="14" value={form.ph_max} onChange={set('ph_max')} />
              </Field>
            </div>

            <div style={{ height: 20 }} />
            <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Constituent Chemicals</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addConstituent}>+ Add Chemical</button>
            </div>
            {form.constituents.length === 0 ? (
              <p className="text-muted" style={{ padding: '12px 0' }}>No constituents added. Click "+ Add Chemical" to list waste components.</p>
            ) : (
              <table style={{ marginTop: 8 }}>
                <thead><tr>
                  <th>Chemical Name</th><th>CAS Number</th><th>% by Weight</th><th></th>
                </tr></thead>
                <tbody>
                  {form.constituents.map((c, i) => (
                    <tr key={i}>
                      <td><input value={c.name} onChange={e => updateConstituent(i, 'name', e.target.value)} placeholder="e.g. Sodium Hydroxide" /></td>
                      <td><input value={c.cas} onChange={e => updateConstituent(i, 'cas', e.target.value)} placeholder="e.g. 1310-73-2" /></td>
                      <td><input type="number" min="0" max="100" value={c.percentage} onChange={e => updateConstituent(i, 'percentage', e.target.value)} /></td>
                      <td><button type="button" className="btn btn-secondary btn-sm" onClick={() => removeConstituent(i)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── STEP 2: Regulatory ──────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <div className="form-section-title">DOT Shipping Information</div>
            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              Complete this section with either the DOT Transport Name or Non-DOT &amp; Non-RCRA Regulated Materials.
            </div>
            <div className="form-grid form-grid-2">
              <Field label="Proper Shipping Name" req error={errors.dot_shipping_name}>
                <input value={form.dot_shipping_name} onChange={set('dot_shipping_name')} placeholder="e.g. Corrosive Liquid, Basic, N.O.S." />
              </Field>
              <Field label="UN Number">
                <input value={form.dot_un_number} onChange={set('dot_un_number')} placeholder="e.g. UN3267" />
              </Field>
              <Field label="Hazard Class / Division">
                <input value={form.dot_hazard_class} onChange={set('dot_hazard_class')} placeholder="e.g. Class 8" />
              </Field>
              <Field label="Packing Group">
                <select value={form.dot_packing_group} onChange={set('dot_packing_group')}>
                  <option value="">— Select if applicable —</option>
                  <option>I</option><option>II</option><option>III</option>
                </select>
              </Field>
            </div>
          </div>
        )}

        {/* ── STEP 3: Handling & Safety ──────────────────────────────── */}
        {step === 3 && (
          <div>
            <div className="form-section-title">Handling Instructions</div>
            <div className="form-grid">
              <Field label="Special Handling Requirements">
                <textarea rows={3} value={form.special_handling} onChange={set('special_handling')} placeholder="List any special storage, temperature, or compatibility requirements" />
              </Field>
              <Field label="Incompatible Materials">
                <textarea rows={2} value={form.incompatible_materials} onChange={set('incompatible_materials')} placeholder="e.g. Strong acids, oxidizers, reactive metals" />
              </Field>
            </div>
            <div style={{ height: 16 }} />
            <div className="form-section-title">Personal Protective Equipment (PPE)</div>
            <Field label="Required PPE" hint="Comma-separated, e.g. Chemical gloves, Safety goggles, Lab coat">
              <input value={form.ppe_required} onChange={set('ppe_required')} placeholder="Chemical-resistant gloves, Safety goggles, Face shield" />
            </Field>
            <div style={{ height: 16 }} />
            <div className="form-section-title">Emergency Contact</div>
            <div className="form-grid form-grid-2">
              <Field label="Emergency Contact Name">
                <input value={form.emergency_contact} onChange={set('emergency_contact')} />
              </Field>
              <Field label="Emergency Phone Number">
                <input type="tel" value={form.emergency_phone} onChange={set('emergency_phone')} />
              </Field>
            </div>
          </div>
        )}

        {/* ── STEP 4: Certification ──────────────────────────────────── */}
        {step === 4 && (
          <div>
            <div className="form-section-title">Certification & Submission</div>
            <div className="alert alert-warn" style={{ marginBottom: 20 }}>
              <strong>Important:</strong> Review all information before submitting. Once submitted, the profile cannot be edited until reviewed by AWWT staff.
            </div>

            <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px', marginBottom: 24, lineHeight: 1.7, fontSize: 13, color: 'var(--ink-mid)' }}>
              <strong>Certification Statement</strong><br /><br />
              I hereby certify that the information provided in this Waste Profile is true, accurate, and complete to the best of my knowledge. I understand that this profile is subject to review and approval by Advanced Waste Water Treatment Corp, and that any misrepresentation may result in rejection of this profile and/or suspension of waste acceptance services. I further certify that I am authorized to submit this profile on behalf of the generator company listed above.
            </div>

            <div className="form-grid form-grid-2" style={{ marginBottom: 20 }}>
              <Field label="Certifier Name" req error={errors.certifier_name}>
                <input value={form.certifier_name} onChange={set('certifier_name')} />
              </Field>
              <Field label="Title / Position" req error={errors.certifier_title}>
                <input value={form.certifier_title} onChange={set('certifier_title')} />
              </Field>
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="checkbox-group" style={{ alignItems: 'flex-start', gap: 12 }}>
                <input type="checkbox" checked={form.certified} onChange={set('certified')} style={{ marginTop: 3, flexShrink: 0 }} />
                <span>I have read and agree to the certification statement above. I certify that all information in this waste profile is accurate and complete.</span>
              </label>
              {errors.certified && <div className="form-error">{errors.certified}</div>}
            </div>

            <div className="form-section-title">Profile Summary</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0' }}>
              {[
                ['Transporter / Customer', form.transporter_customer],
                ['Company',               form.company_name],
                ['Contact',               form.contact_name],
                ['Waste Name',            form.waste_name],
                ['Physical State',        form.physical_state],
                ['Waste Source(s)',        form.waste_sources.join(', ') || '—'],
                ['Lab Files',             totalFiles > 0 ? `${totalFiles} file${totalFiles !== 1 ? 's' : ''} attached` : '—'],
              ].map(([k, v]) => (
                <div key={k} className="detail-row">
                  <div className="detail-label">{k}</div>
                  <div className="detail-value">{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Navigation ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {step > 0 && (
              <button className="btn btn-secondary" onClick={() => setStep(s => s - 1)}>← Back</button>
            )}
            <button className="btn btn-secondary" onClick={saveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            {saveMsg && <span style={{ fontSize: 12, color: saveMsg.includes('failed') ? '#c62828' : 'var(--forest)' }}>{saveMsg}</span>}
          </div>
          <div>
            {step < STEPS.length - 1 ? (
              <button className="btn btn-primary" onClick={handleNext}>Next →</button>
            ) : (
              <button className="btn btn-success" onClick={handleSubmit} disabled={submitting || !form.certified}>
                {submitting ? 'Submitting…' : 'Submit Profile'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, req, hint, error, children }) {
  return (
    <div className="form-group">
      <label>{label}{req && <span className="req"> *</span>}</label>
      {children}
      {hint  && <span className="form-hint">{hint}</span>}
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}
